import { useRegisterSW } from 'virtual:pwa-register/react';
import { RefreshCw, X } from 'lucide-react';

/**
 * Aviso de nova versão da PWA (F5-T13, PLAN §37).
 *
 * O Service Worker é registrado com `registerType: 'prompt'` e `skipWaiting: false`
 * de propósito: uma troca automática no meio de um download em andamento derrubaria
 * a fila. O usuário decide quando recarregar.
 *
 * Atualizar o SW **não** toca em IndexedDB nem em OPFS — só o shell é substituído.
 */
export function UpdatePrompt(): React.ReactElement | null {
  const {
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(_url, registration) {
      if (!registration) return;
      // Checagem periódica só faz sentido com rede; sem ela o fetch falha em silêncio.
      setInterval(
        () => {
          if (navigator.onLine) void registration.update();
        },
        60 * 60 * 1000,
      );
    },
    onRegisterError(error) {
      console.error('[pwa] falha ao registrar o Service Worker', error);
    },
  });

  if (!needRefresh) return null;

  return (
    <div
      role="alert"
      className="fixed inset-x-0 bottom-0 z-50 px-safe pb-safe"
      style={{ paddingBottom: 'calc(var(--safe-bottom) + 5.5rem)' }}
    >
      <div className="mx-auto flex max-w-md items-center gap-3 rounded-xl bg-slate-800 p-3 shadow-lg ring-1 ring-slate-700">
        <RefreshCw size={18} className="shrink-0 text-sky-400" aria-hidden />

        <p className="flex-1 text-sm">
          Nova versão disponível.
          <span className="block text-xs text-slate-400">
            Seus downloads e progresso são preservados.
          </span>
        </p>

        <button
          type="button"
          onClick={() => void updateServiceWorker(true)}
          className="min-h-11 shrink-0 rounded-lg bg-sky-500 px-4 text-sm font-medium text-slate-950 active:bg-sky-600"
        >
          Atualizar
        </button>

        <button
          type="button"
          onClick={() => setNeedRefresh(false)}
          aria-label="Dispensar aviso de atualização"
          className="min-h-11 min-w-11 shrink-0 rounded-lg text-slate-400 active:bg-slate-700"
        >
          <X size={18} aria-hidden className="mx-auto" />
        </button>
      </div>
    </div>
  );
}
